<template>
  <v-card class="pa-4" flat>
    <div class="d-flex justify-space-between align-center mb-4">
      <v-btn @click="openAddDialog" outlined class="pa-2 btn-get-code rounded-lg">
        <v-icon left>mdi-plus</v-icon>
        افزودن قیمت‌گذاری چندگانه
      </v-btn>
    </div>

    <!-- جدول نمایش اطلاعات -->
    <v-progress-linear v-show="loading" class="mb-2" indeterminate />
    <vue-table
      :data="smartPrices"
      :headers="tableColumns"
      :loading.sync="loading"
      @change="onPagination"
    >
      <!-- اسلات فیلترهای یکپارچه جدول -->
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              class="rounded-lg"
              dense hide-details outlined
              :label="'تعداد در هر صفحه'"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="3" md="3">
            <v-select
              v-model="props.is_enabled"
              :items="statusOptions"
              label="وضعیت"
              dense hide-details outlined clearable class="rounded-lg"
              @change="() => onPagination(props)"
            ></v-select>
          </v-col>
        </v-row>
      </template>

      <template v-slot:index="{item}">               
        {{ persian(item.index + 1 + (smartPrices?.current_page - 1) * smartPrices?.per_page) }}
      </template>

      <!-- اسلات وضعیت -->
      <template v-slot:is_enabled="{ item }">
        <v-chip :color="item.is_enabled ? 'green' : 'red'" small text-color="white">
          {{ item.is_enabled ? 'فعال' : 'غیرفعال' }}
        </v-chip>
      </template>
      <template v-slot:variant.image_src="{ item }">
        <div class="d-flex flex-column align-center pa-2">
          <v-img :src="item.variant?.image_src" max-width="64" max-height="64" contain />
        </div>
      </template>

      <!-- تعداد حالت‌ها -->
      <template v-slot:rules_count="{item}">
        <v-chip class="px-2 py-3 rounded-pill" color="blue lighten-4">
          <span class="text-body-2 font-weight-bold">
            {{ persian(item.rules ? item.rules.length : 0) }} حالت
          </span>
        </v-chip>
      </template>

      <template v-slot:variant_id="{item}">
        <v-chip class="px-2 py-3 rounded-lg" color="transparent">
          <span class="text-body-2 font-weight-bold">{{ persian(item.variant_id) }}</span>
          <v-btn icon small @click.stop="copyID(item.variant_id)" class="ms-2" color="#B5C5FD">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </v-chip>
      </template>

      <template v-slot:product_id="{item}">
        <v-chip class="px-2 py-3 rounded-lg" color="transparent">
          <span class="text-body-2 font-weight-bold">{{ persian(item.product_id) }}</span>                  
          <v-btn icon small @click.stop="copyID(item.product_id)" class="ms-2" color="#B5C5FD">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </v-chip>
      </template>

      <template v-slot:action="{ item }">
        <action-buttons :data="item" :meta="metabutton" />
      </template>
    </vue-table>

    <!-- مدال افزودن/ویرایش -->
    <custom-popup
      v-model="showDialog"
      :cons="{ title: isEditing ? 'ویرایش قیمت‌گذاری چندگانه' : 'افزودن قیمت‌گذاری چندگانه' }"
      max-width="900"
      :reloadable="false"
      :persistent="true"
      @close="resetForm"
    >
      <v-card class="pa-4" flat>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="formData.product_id"
                label="شناسه محصول (Product ID)"
                type="number"
                :rules="[(v) => !!v || 'وارد کردن شناسه محصول الزامی است']"
                outlined dense
                :readonly="isEditing"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="formData.variant_id"
                label="شناسه تنوع (Variant ID)"
                :rules="[(v) => !!v || 'وارد کردن شناسه تنوع الزامی است']"
                type="number"
                outlined dense
                :readonly="isEditing"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-expansion-panels v-model="activePanel" class="mb-6" accordion>
            <v-expansion-panel 
              v-for="(rule, index) in formData.rules" 
              :key="index"
              class="elevation-2 rounded-lg border-primary mb-2"
            >
              <v-expansion-panel-header class="bg-grey-lighten-4 py-2 px-4">
                <template v-slot:default>
                  <div class="d-flex justify-space-between align-center w-100 pl-4">
                    <span class="text-subtitle-2 font-weight-bold">حالت {{ index + 1 }}</span>
                    <v-btn v-if="formData.rules.length > 1" icon color="error" small @click.stop="removeRule(index)">
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-expansion-panel-header>
              
              <v-expansion-panel-content class="pt-4">
                <v-card class="elevation-0" color="transparent">
                  <v-row class="mb-2 bg-grey-lighten-4 pa-2 rounded align-center mx-0">
                    <v-col cols="12" class="d-flex flex-column justify-center align-start border-bottom">
                      <div class="text-caption text-grey mb-1">روش ارسال</div>
                      <div class="d-flex align-center flex-wrap">
                        <v-checkbox v-model="rule.is_fbd_active" label="دیجی‌کالا" dense hide-details class="mt-0 mr-4"></v-checkbox>
                        <v-checkbox v-model="rule.is_fbs_active" label="فروشنده" dense hide-details class="mt-0 mr-4"></v-checkbox>
                        <v-checkbox v-model="rule.is_three_hour_delivery_active" label="۳ ساعته" dense hide-details class="mt-0"></v-checkbox>
                      </div>
                    </v-col>
                    
                    <v-col cols="12" class="pa-0"><v-divider></v-divider></v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        v-model="rule.lead_time"
                        :items="leadTimeOptions"
                        label="مهلت تحویل به انبار دیجی‌کالا"
                        dense outlined hide-details
                        :disabled="!rule.is_fbd_active"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="rule.delivery_time"
                        :items="deliveryTimeOptions"
                        label="مهلت تحویل به خریدار"
                        dense outlined hide-details
                        :disabled="!rule.is_fbs_active && !rule.is_fbd_active" 
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="rule.seller_lead_time"
                        :items="sellerLeadTimeOptions"
                        label="بازه زمانی از ثبت سفارش تا تحویل"
                        dense outlined hide-details
                        :disabled="!rule.is_fbs_active && !rule.is_three_hour_delivery_active"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-card>

                <v-row class="mt-2">
                  <v-col cols="12" md="6">
                    <price-input 
                      v-model="rule.min_price" 
                      label="حداقل قیمت" 
                      :rules="[ (v) => !!v || 'الزامی است']"
                      dense :min="0"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <price-input
                      v-model="rule.max_price"
                      label="حداکثر قیمت"
                      :rules="[ 
                        (v) => !!v || 'الزامی است',
                        (v) => Number(String(v).replace(/,/g, '')) > Number(String(rule.min_price || 0).replace(/,/g, '')) || 'باید بزرگتر از حداقل قیمت باشد'
                      ]"
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <price-input
                      v-model.number="rule.price_step"
                      label="کسر قیمت"
                      :rules="[    
                        (v) => !!v || 'الزامی است',        
                        (v) => Number(String(v).replace(/,/g, '')) >= 2000 || 'نمی‌تواند کمتر از 2000 باشد'
                      ]"
                      min="2000" dense
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-switch
                      v-model="rule.has_discount"
                      :true-value="1" :false-value="0"
                      :label="rule.has_discount===1 ? 'بر حسب تخفیف' : 'بر حسب کسر قیمت'"
                      color="primary" hide-details
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div class="d-flex justify-center mb-6">
            <v-btn class="btn-get-code rounded-lg" @click="addRule">
              <v-icon left>mdi-plus</v-icon>
              افزودن حالت قیمت‌گذاری جدید
            </v-btn>
          </div>
          
          <v-divider class="mb-4"></v-divider>
          
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="formData.is_enabled"
                label="وضعیت فعالیت کلی برای این تنوع"
                color="success" hide-details
              ></v-switch>
            </v-col>
          </v-row>

          <div class="d-flex flex-row justify-space-between mt-4">
            <v-btn
              :loading="saving"
              class="btn-get-code rounded-lg px-6"
              :disabled="!valid || !validComputed" 
              @click="saveData"
            >
              <v-icon left>{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
              {{ isEditing ? 'ویرایش' : 'افزودن' }}
            </v-btn>          
            <v-btn
              :loading="saving"
              class="btn-delete rounded-lg px-6"
              @click="showDialog = false"
            >
              <v-icon left>mdi-close</v-icon>
              لغو
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </custom-popup>
        <!-- مدال نمایش لیست قیمت‌ها -->
    <custom-popup
      v-model="showRulesDialog"
      :cons="{ title: 'جزئیات حالت‌های قیمت‌گذاری' }"
      max-width="800"
      :reloadable="false"
    >
      <v-card class="pa-4" flat color="#f8f9fa">
        <v-alert v-if="!selectedItemForShow?.rules?.length" type="info" text outlined class="rounded-lg">
          هیچ حالتی برای این تنوع ثبت نشده است.
        </v-alert>
        
        <v-row v-else>
          <v-col cols="12" v-for="(rule, index) in selectedItemForShow.rules" :key="index">
            <v-card class="rounded-xl elevation-2 pa-5 border-left-primary">
              <div class="d-flex justify-space-between align-center mb-3">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="32" class="white--text font-weight-bold ml-2">
                    {{ persian(index + 1) }}
                  </v-avatar>
                  <h3 class="text-h6 primary--text font-weight-bold mb-0">حالت قیمت‌گذاری</h3>
                </div>
                <v-chip :color="rule.has_discount ? 'purple' : 'indigo'" dark small class="font-weight-bold px-3">
                  <v-icon right small class="mr-n1 ml-1">
                    {{ rule.has_discount ? 'mdi-brightness-percent' : 'mdi-currency-usd-off' }}
                  </v-icon>
                  {{ rule.has_discount ? 'بر حسب تخفیف' : 'بر حسب کسر قیمت' }}
                </v-chip>
              </div>
              
              <v-divider class="mb-4"></v-divider>
              
              <v-row>
                <v-col cols="12" md="4" class="py-2">
                  <div class="text-caption text-grey-darken-1 mb-1">حداقل قیمت</div>
                  <div class="text-body-1 font-weight-bold">
                    {{ Number(rule.min_price).toLocaleString('fa-IR') }} <span class="text-caption text-grey">ریال</span>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="py-2">
                  <div class="text-caption text-grey-darken-1 mb-1">حداکثر قیمت</div>
                  <div class="text-body-1 font-weight-bold">
                    {{ Number(rule.max_price).toLocaleString('fa-IR') }} <span class="text-caption text-grey">ریال</span>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="py-2">
                  <div class="text-caption text-grey-darken-1 mb-1">کسر قیمت / گام</div>
                  <div class="text-body-1 font-weight-bold error--text">
                    {{ Number(rule.price_step).toLocaleString('fa-IR') }} <span class="text-caption text-grey">ریال</span>
                  </div>
                </v-col>
              </v-row>
              
              <v-card color="grey lighten-4" flat class="rounded-lg mt-4 pa-3">
                <div class="text-caption text-grey-darken-2 mb-2 font-weight-bold">
                  <v-icon small color="grey darken-1" class="ml-1">mdi-truck-delivery-outline</v-icon>
                  روش‌های ارسال فعال:
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <v-chip v-if="rule.is_fbd_active" color="success" outlined small class="font-weight-bold ml-2 mb-2">
                    ارسال دیجی‌کالا (تامین: {{ persian(rule.lead_time) }} روز)
                  </v-chip>
                  
                  <v-chip v-if="rule.is_fbs_active" color="orange darken-2" outlined small class="font-weight-bold ml-2 mb-2">
                    ارسال فروشنده 
                    (ارسال: {{ rule.delivery_time === 0 ? 'همان‌روز' : persian(rule.delivery_time) + ' روز' }} |
                    تامین: {{ persian(rule.seller_lead_time) }} ساعت)
                  </v-chip>
                  
                  <v-chip v-if="rule.is_three_hour_delivery_active" color="info" outlined small class="font-weight-bold mb-2">
                    ارسال ۳ ساعته
                  </v-chip>
                </div>
              </v-card>
            </v-card>
          </v-col>
        </v-row>

        <div class="d-flex justify-end mt-4">
          <v-btn color="grey darken-2" outlined class="rounded-lg px-6" @click="showRulesDialog = false">
            بستن
          </v-btn>
        </div>
      </v-card>
    </custom-popup>

  </v-card>
</template>

<script>
import ActionButtons from "@/view/components/adapters/qrList/ordersListButton.vue";
import PriceInput from "../../../core/plugins/TitleRibbon/pages/pickers/priceInput.vue";

export default {
  name: "SmartPricesTab",
  components: { ActionButtons, PriceInput },
  data() {
    return {
      loading: false,
      saving: false,
      showDialog: false,
      isEditing: false,
      valid: false,
      activePanel: 0,
      smartPrices: [],
      showRulesDialog: false,
      selectedItemForShow: null,
      leadTimeOptions: [
        { text: '۱ روز', value: 1 },
        { text: '۲ روز', value: 2 },
        { text: '۳ روز', value: 3 },
      ],
      deliveryTimeOptions: [
        { text: 'همان‌روز', value: 0 },
        { text: '۱ روز', value: 1 },
        { text: '۲ روز', value: 2 },
      ],
      sellerLeadTimeOptions: [
        { text: 'تا ۲ ساعت', value: 2 },
        { text: 'تا ۳ ساعت', value: 3 },
        { text: 'تا ۴ ساعت', value: 4 },
        { text: 'تا ۵ ساعت', value: 5 },
        { text: 'تا ۲۴ ساعت', value: 24 },
      ],
      statusOptions: [
        { text: "همه", value: null },
        { text: "فعال", value: true },
        { text: "غیرفعال", value: false },
      ],

      tableProps: {
        page: 1,
        length: 10,
        is_enabled: null,
      },
      
      formData: {
        id: null,
        variant_id: null,
        product_id: null,
        is_enabled: true,
        rules: []
      },
    };
  },

  computed: {
    validComputed(){
      return !!this.formData.product_id && !!this.formData.variant_id && this.formData.rules.length > 0;
    },
    tableColumns() {
      return [
        { value: "index", text: "ردیف", align: "center", sortable: true },
        { value: "product_id", text: "شناسه محصول", align: "center", sortable: true },
        { value: "variant.image_src", text: "تصویر محصول", align: "center", sortable: false },
        { value: "variant_id", text: "شناسه تنوع", align: "center", sortable: true },
        { value: "rules_count", text: "تعداد حالت‌ها", align: "center", sortable: false },
        { value: "is_enabled", text: "وضعیت کلی", align: "center", sortable: false },
        { value: "action", text: "عملیات", align: "center", sortable: false, event: "actions" },
      ];
    },
    metabutton() {
      return {
        buttons: [
          { name: "show", label: "نمایش قیمت‌ها", icon: "eye", class: "btn-show rounded-pill", action: this.actions },
          { name: "edit", label: "ویرایش", icon: "pencil", class: "btn-edit rounded-pill", action: this.actions },
          { name: "delete", label: "حذف", icon: "delete", class: "btn-delete rounded-pill", action: this.actions },
        ],
      };
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    copyID(textToCopy) {
      const text = String(textToCopy); 
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
            this.$toast.success("با موفقیت کپی شد");
        }).catch(() => {
            this.$toast.error("خطا در کپی");
        });
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        this.$toast.success("با موفقیت کپی شد");
      }
    },

    getEmptyRule() {
      return {
        min_price: 0,
        max_price: 0,
        price_step: 20000,
        has_discount: 0,
        is_fbd_active: true,
        is_fbs_active: true,
        is_three_hour_delivery_active: true,
        lead_time: 1,
        delivery_time: 0,
        seller_lead_time: 3,
      };
    },

    addRule() {
      this.formData.rules.push(this.getEmptyRule());
      this.$nextTick(() => {
        this.activePanel = this.formData.rules.length - 1;
      });
    },

    removeRule(index) {
      this.formData.rules.splice(index, 1);
      if (this.activePanel >= this.formData.rules.length) {
        this.activePanel = Math.max(0, this.formData.rules.length - 1);
      }
    },

    fetchData() {
      this.loading = true;
      this.$api.get("/v1/digikala/multi-pricing/", { params: this.tableProps })
        .then(({ data }) => {
          this.smartPrices = data.data || data; 
        })
        .catch(() => this.$toast.error("خطا در دریافت اطلاعات"))
        .finally(() => this.loading = false);
    },

    onPagination(e) {
      if (e && typeof e === 'object' && !Array.isArray(e)) {
        this.tableProps = { ...this.tableProps, ...e };
        if ('is_enabled' in e) this.tableProps.page = 1;
      }
      this.fetchData();
    },

    openAddDialog() {
      this.isEditing = false;
      this.formData = {
        id: null,
        variant_id: null,
        product_id: null,
        is_enabled: true,
        rules: [this.getEmptyRule()]
      };
      this.activePanel = 0;
      this.showDialog = true;
    },

    resetForm() {
      if (this.$refs.form) this.$refs.form.resetValidation();
    },

    validateDuplicates() {
      const parsedRules = this.formData.rules.map(rule => ({
        min: Number(String(rule.min_price || 0).replace(/,/g, '')),
        max: Number(String(rule.max_price || 0).replace(/,/g, '')),
        fbd: Boolean(rule.is_fbd_active),
        fbs: Boolean(rule.is_fbs_active),
        three_hour: Boolean(rule.is_three_hour_delivery_active)
      }));

      for (let i = 0; i < parsedRules.length; i++) {
        for (let j = i + 1; j < parsedRules.length; j++) {
          const r1 = parsedRules[i];
          const r2 = parsedRules[j];

          if (r1.fbd === r2.fbd && r1.fbs === r2.fbs && r1.three_hour === r2.three_hour) {
            this.$toast.error(`ترکیب روش‌های ارسال در حالت ${i + 1} و ${j + 1} تکراری است.`);
            this.activePanel = i; 
            return false;
          }
          if (r1.min === r2.min && r1.max === r2.max) {
            this.$toast.error(`بازه قیمتی در حالت ${i + 1} و ${j + 1} تکراری است.`);
            this.activePanel = i; 
            return false;
          }
        }
      }
      return true;
    },

    saveData() {
      if (!this.$refs.form.validate() || !this.validateDuplicates()) return;

      this.saving = true;
      const payload = {
        product_id: this.formData.product_id,
        variant_id: this.formData.variant_id,
        is_enabled: this.formData.is_enabled,
        rules: this.formData.rules.map(rule => ({
          ...rule,
          min_price: Number(String(rule.min_price).replace(/,/g, '')),
          max_price: Number(String(rule.max_price).replace(/,/g, '')),
          price_step: Number(String(rule.price_step).replace(/,/g, '')),
        }))
      };

      const method = this.isEditing ? "put" : "post";
      const url = this.isEditing
        ? `/v1/digikala/multi-pricing/${this.formData.id}`
        : "/v1/digikala/multi-pricing/store";

      this.$api[method](url, payload)
        .then(() => {
          this.$toast.success(this.isEditing ? "با موفقیت ویرایش شد" : "با موفقیت ذخیره شد");
          this.showDialog = false;
          this.fetchData();
        })
        .catch(() => this.$toast.error("خطا در ذخیره اطلاعات"))
        .finally(() => this.saving = false);
    },

    actions(data, type) {
      if (type === "edit") this.editAction(data);
      else if (type === "delete") this.deleteAction(data);
      else if( type === "show") this.showAction(data);
    },
    showAction(data){
        this.selectedItemForShow = data;
        this.showRulesDialog = true;
    },
    editAction(data) {
      this.isEditing = true;
      this.formData = { 
        id: data.id,
        product_id: data.product_id,
        variant_id: data.variant_id,
        is_enabled: data.is_enabled,
        rules: data.rules && data.rules.length ? JSON.parse(JSON.stringify(data.rules)) : [this.getEmptyRule()]
      }; 
      this.activePanel = 0;
      this.showDialog = true;
    },

    async deleteAction(data) {
      const confirm = await this.$modal.yesNo("آیا از حذف این مورد اطمینان دارید؟");
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.delete(`/v1/digikala/multi-pricing/${data.id}`);
        this.$toast.success("با موفقیت حذف شد");
        await this.fetchData();
      } catch {
        this.$toast.error("خطا در حذف اطلاعات");
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.gap-4 { gap: 16px; }
.border-bottom { border-bottom: 1px solid #e0e0e0; padding-bottom: 8px; margin-bottom: 8px; }
.border-left-primary {
  border-right: 4px solid #1976d2 !important; 
}
</style>
